<template>
  <small-modal
    class="onboarding-settings-modal"
    :close-on-background-click="false"
    @close="$emit('close')"
  >
    <div class="onboarding-settings-wrapper">
      <h1 class="title">{{ $t("onboarding.onboardingModal.title") }}</h1>
      <div class="settings">
        <div class="setting">
          <p>{{ $t("onboarding.onboardingModal.language") }}</p>
          <div class="language-buttons">
            <button
              class="lang-button first-button"
              :class="{
                selected:
                  preferredLanguageUI !== 'fr' && preferredLanguageUI !== 'es',
              }"
              @click="changeLanguage('en')"
            >
              {{ $t("onboarding.onboardingModal.en") }}
              <img
                class="country-image"
                src="@/assets/svg/settings/english-flag.svg"
                alt="uk"
              />
            </button>
            <button
              class="lang-button second-button"
              :class="{ selected: preferredLanguageUI === 'fr' }"
              @click="changeLanguage('fr')"
            >
              {{ $t("onboarding.onboardingModal.fr") }}
              <img
                class="country-image"
                src="@/assets/svg/settings/french-flag.svg"
                alt="fr"
              />
            </button>
            <button
              class="lang-button third-button"
              :class="{ selected: preferredLanguageUI === 'es' }"
              @click="changeLanguage('es')"
            >
              {{ $t("onboarding.onboardingModal.es") }}
              <img
                class="country-image"
                src="@/assets/spanish-flag.png"
                alt="es"
              />
            </button>
          </div>
        </div>
        <div class="setting">
          <p>{{ $t("onboarding.onboardingModal.mainColor") }}</p>
          <div class="color-buttons">
            <input v-model="mainColorValue" class="color-code" maxlength="7" />
            <button
              class="value-color"
              :style="`background-color:${mainColorValue || '#7B01B5'}`"
              @click="showPicker(`main`)"
            />
          </div>
        </div>
        <div class="setting">
          <p>{{ $t("onboarding.onboardingModal.textColor") }}</p>
          <div class="color-buttons">
            <input v-model="textColorValue" class="color-code" maxlength="7" />
            <button
              class="value-color"
              :style="`background-color:${textColorValue || '#ffffff'}`"
              @click="showPicker(`text`)"
            />
          </div>
        </div>
      </div>
      <div v-if="preferredLanguageUI === 'fr'" class="video">
        <div
          class="vidjet-video-wrapper"
          style="
            padding-bottom: 62.5%;
            position: relative;
            overflow: hidden;
            height: auto;
            margin: 0px auto;
          "
        >
          <iframe
            class="vidjet-embed-iframe"
            data-vidjet-campaign-id="b6c071fa-ff8f-4dfa-bd71-9e400caa0dc4"
            src="https://player.vidjet.io/ea450a97-86d5-47c2-8dde-5cbed280663b/b6c071fa-ff8f-4dfa-bd71-9e400caa0dc4?backendCall=true&onboardingModal=true"
            style="
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              border-radius: 10px;
            "
            allow="clipboard-read; clipboard-write; fullscreen"
            frameborder="0"
          ></iframe>
        </div>
        <a target="_blank" :href="youtubeLink" class="youtube-button"
          ><YoutubeLogo />Voir sur YouTube</a
        >
      </div>
      <div v-else-if="preferredLanguageUI === 'es'" class="video">
        <div
          class="vidjet-video-wrapper"
          style="
            padding-bottom: 62.5%;
            position: relative;
            overflow: hidden;
            height: auto;
            margin: 0px auto;
          "
        >
          <iframe
            class="vidjet-embed-iframe"
            data-vidjet-campaign-id="560649af-af6b-43f3-84b9-895bdc4e97ac"
            src="https://player.vidjet.io/ea450a97-86d5-47c2-8dde-5cbed280663b/560649af-af6b-43f3-84b9-895bdc4e97ac?backendCall=true&onboardingModal=true"
            style="
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              border-radius: 10px;
            "
            allow="clipboard-read; clipboard-write; fullscreen"
            frameborder="0"
          ></iframe>
        </div>
        <a target="_blank" :href="youtubeLink" class="youtube-button"
          ><YoutubeLogo />Ver en youtube</a
        >
      </div>
      <div v-else class="video">
        <div
          class="vidjet-video-wrapper"
          style="
            padding-bottom: 62.5%;
            position: relative;
            overflow: hidden;
            height: auto;
            margin: 0px auto;
          "
        >
          <iframe
            class="vidjet-embed-iframe"
            data-vidjet-campaign-id="d81ec863-7e91-4f9d-b04e-146e08c576e5"
            src="https://player.vidjet.io/ea450a97-86d5-47c2-8dde-5cbed280663b/d81ec863-7e91-4f9d-b04e-146e08c576e5?backendCall=true&onboardingModal=true"
            style="
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              border-radius: 10px;
            "
            allow="clipboard-read; clipboard-write; fullscreen"
            frameborder="0"
          ></iframe>
        </div>
        <a target="_blank" :href="youtubeLink" class="youtube-button"
          ><YoutubeLogo />See on youtube</a
        >
      </div>
      <div class="main-buttons">
        <a
          class="main-button"
          href="https://help.vidjet.io/en/"
          target="_blank"
        >
          <OpenIcon class="open-icon" />{{
            $t("onboarding.onboardingModal.tutorials")
          }}
        </a>
        <a
          class="main-button"
          href="https://calendly.com/demo-vidjet/30min?month=2025-06"
          target="_blank"
        >
          <OpenIcon class="open-icon" />{{
            $t("onboarding.onboardingModal.call")
          }}
        </a>
        <button class="main-button import-button" @click="openVideoLibrary">
          <PlusIcon class="plus-icon" />{{
            $t("onboarding.onboardingModal.import")
          }}
        </button>
      </div>
    </div>
    <div
      v-if="displayPicker"
      key="color-picker"
      v-click-outside="hidePicker"
      class="vidjet-color-picker"
    >
      <colorPicker
        :value="currentColor"
        @input="onPickerChange($event, pickerType)"
      />
    </div>
  </small-modal>
</template>

<script>
import SmallModal from "@/components/shared/SmallModal.vue";
import OpenIcon from "@/assets/svg/open-icon-big.svg?inline";
import PlusIcon from "@/assets/svg/plus-icon.svg?inline";
import YoutubeLogo from "@/assets/youtube-logo.svg?inline";
import { Chrome } from "vue-color";
import { mapState } from "vuex";
export default {
  components: {
    SmallModal,
    colorPicker: Chrome,
    OpenIcon,
    PlusIcon,
    YoutubeLogo,
  },
  data() {
    return {
      mainColorValue: "#7B01B5",
      textColorValue: "#FFFFFF",
      pickerType: "",
      displayPicker: false,
    };
  },
  computed: {
    ...mapState(["site", "user"]),
    currentColor() {
      return this.pickerType === "main"
        ? this.mainColorValue
        : this.textColorValue;
    },
    preferredLanguageUI() {
      return this.$i18n.locale || "en";
    },

    preferredLanguageSaved() {
      return this.user?.user?.preferredLanguage || "en";
    },
    youtubeLink() {
      const lang = this.$i18n.locale;
      if (lang === "fr") return "https://www.youtube.com/watch?v=0aH9z70uL9A";
      if (lang === "es") return "https://www.youtube.com/watch?v=9ho5ghdqLiM";
      return "https://www.youtube.com/watch?v=Nox42291ZZ0";
    },
  },

  mounted() {
    this.mainColorValue = this.site?.site?.mainColor || "#7B01B5";
    this.textColorValue = this.site?.site?.textColor || "#FFFFFF";
  },
  methods: {
    showPicker(type) {
      this.pickerType = type;
      this.displayPicker = true;
    },
    hidePicker() {
      this.displayPicker = false;
    },
    onPickerChange(color) {
      const hex = color.hex;
      if (this.pickerType === "main") {
        this.mainColorValue = hex;
        this.updateSiteColor("mainColor", hex);
      } else if (this.pickerType === "text") {
        this.textColorValue = hex;
        this.updateSiteColor("textColor", hex);
      }
    },

    async changeLanguage(langShort) {
      this.$i18n.locale = langShort;

      if (langShort !== this.user?.user?.preferredLanguage) {
        try {
          await this.$store.dispatch("user/updateUser", {
            userId: this.user.user._id,
            dataToUpdate: { preferredLanguage: langShort },
          });
          this.$notify({
            ignoreDuplicates: true,
            title: "Success",
            text: this.$t("shared.toastMessage.languageUpdated"),
            type: "success",
          });
        } catch (e) {
          this.$notify({
            title: this.$t("shared.toastMessage.error"),
            text: this.$t("shared.toastMessage.tryAgain"),
            type: "error",
          });
          console.error(e);
        }
      }
    },
    async updateSiteColor(key, value) {
      try {
        await this.$store.dispatch("site/updateSite", {
          siteId: this.site.site._id,
          dataToUpdate: {
            [key]: value,
          },
        });
      } catch (e) {
        console.error(e);
      }
    },
    openVideoLibrary() {
      this.$emit("open-video-library");
    },
  },
};
</script>

<style lang="scss">
.onboarding-settings-modal .small-modal.bigger {
  width: 800px;
  height: 680px;
  overflow: hidden;
  background-color: #faeaff;
}
</style>

<style lang="scss" scoped>
.onboarding-settings-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 36px 0;
}

.title {
  @include base-font;
  @include font-big;
  font-weight: 600;
  margin-bottom: 28px;
}

.settings {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 70%;
  margin-bottom: 16px;
  p {
    @include base-font;
    @include font-small;
  }
}

.setting {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.language-buttons {
  display: flex;
}
.lang-button {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid $medium-grey;
  padding: 8px 16px;
  background: white;
  @include base-font;
  @include font-smaller;
  color: $medium-grey;

  &.selected {
    background-color: $dark-purple;
    color: white;
    border: 1px solid $dark-purple;
    z-index: 1;
  }
}

.first-button {
  border-radius: 2px 0 0 2px;
}
.third-button {
  border-radius: 0 2px 2px 0;
}

.color-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-code {
  border: 1px solid $medium-grey;
  padding: 8px 16px;
  background: transparent;
  @include base-font;
  @include font-smaller;
  color: $medium-grey;
  border-radius: 2px;
  outline: none;
  width: 90px;
  box-sizing: border-box;
}

.value-color {
  margin: 0;
}
.country-image {
  width: 14px;
  height: 14px;
}

.video {
  width: 70%;
  margin-bottom: 16px;
  position: relative;
}

.main-buttons {
  width: 70%;
  display: flex;
  justify-content: space-between;
}

.main-button {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid $dark-purple;
  padding: 8px 16px;
  background: white;
  @include base-font;
  @include font-smaller;
  color: $dark-purple;
  border-radius: 4px;
  text-decoration: none;
  &:hover {
    background-color: $light-pink;
  }
}

.open-icon {
  margin-bottom: 2px;
}

.import-button {
  background-color: $dark-purple;
  color: white;
  &:hover {
    background-color: $light-pink;
    color: $dark-purple;
    .plus-icon {
      path {
        fill: $dark-purple;
      }
    }
  }
}

.plus-icon {
  path {
    fill: white;
  }
}

.youtube-button {
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0px 6px;
  background-color: white;
  border: 1px solid #ff0000;
  border-radius: 2px;
  color: #ff0000;
  text-decoration: none;
  width: fit-content;
  position: absolute;
  top: 14px;
  right: 8px;
  cursor: pointer;
}
</style>
